<template>
  <div class="attachment-list" ref="list">
    <attachment-stream
      v-if="showStream"
      :files="files"
      :param="{ messageId, commentId }"
      :startIndex="streamIndex"
      :visible="showStream"
      :isMobile="isMobile"
      @close="closeStream"
    />
    <template v-if="files.length === 1">
      <div class="single-attachment-background" @click="openStream(0)">
        <div class="single-attachment">
          <attachment-img
            :image="files[0]"
            :param="{ messageId, commentId }"
            :class="singleOrientation"
            @load.native="onSingleImageLoad"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="attachment-grid" :class="gridClass">
        <div
          v-for="(file, idx) in files.slice(0, 10)"
          :key="idx"
          class="attachment-grid-item"
          @click="openStream(idx)"
        >
          <attachment-img :image="file" :param="{ messageId, commentId }" />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  type PropType,
} from "vue";
import { useAppStore } from "@/store";
import AttachmentImg from "../AttachmentImg.vue";
import AttachmentStream from "./AttachmentStream.vue";
import type { Attachment } from "@/model";

export default defineComponent({
  components: { AttachmentImg, AttachmentStream },
  props: {
    files: { type: Array as PropType<Attachment[]>, default: () => [] },
    messageId: { type: String, default: "" },
    commentId: { type: String, default: "" },
  },
  setup() {
    // NOTE: 旧クラスではクラスプロパティ name = "attachment-list" が data として
    // 保持されていた（コンポーネント名オプションではない）ため、その挙動を踏襲する
    const name = ref<string>("attachment-list");

    const singleOrientation = ref<"vertical" | "horizontal">("horizontal");
    const showStream = ref(false);
    const streamIndex = ref(0);
    const gridClass = ref<string>("grid-5");
    const gridMaxWidth = ref<number>(1000);

    // 旧 this.$refs.list 用の template ref
    const list = ref<HTMLElement>();

    const isMobile = computed((): boolean => {
      return useAppStore().isMobile;
    });

    const onSingleImageLoad = (e: Event) => {
      const img = e.target as HTMLImageElement;
      singleOrientation.value = img.naturalHeight > img.naturalWidth ? "vertical" : "horizontal";
    };

    const openStream = (idx: number) => {
      streamIndex.value = idx;
      showStream.value = true;
    };
    const closeStream = () => {
      showStream.value = false;
    };

    const updateGridClass = () => {
      const el = list.value;
      if (!el) return;
      const width = el.offsetWidth;
      if (width < 420) {
        gridClass.value = "grid-2";
        gridMaxWidth.value = 280;
      } else if (width < 560) {
        gridClass.value = "grid-3";
        gridMaxWidth.value = 420;
      } else if (width < 720) {
        gridClass.value = "grid-4";
        gridMaxWidth.value = 560;
      } else {
        gridClass.value = "grid-5";
        gridMaxWidth.value = 1000;
      }
    };

    onBeforeMount(() => {
      updateGridClass();
    });

    onMounted(() => {
      nextTick(() => {
        updateGridClass();
      });
      window.addEventListener("resize", updateGridClass);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateGridClass);
    });

    return {
      name,
      singleOrientation,
      showStream,
      streamIndex,
      gridClass,
      gridMaxWidth,
      list,
      isMobile,
      onSingleImageLoad,
      openStream,
      closeStream,
      updateGridClass,
    };
  },
});
</script>

<style scoped>
.attachment-list {
  margin: 0 0 8px 0;
  width: 100%;
  color: #707070;
  user-select: none;
  caret-color: transparent;
}
.single-attachment-background {
  width: 100%;
  height: 100%;
  cursor: pointer;
  background-color: #ecf1f4;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
}
.single-center {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
.single-attachment {
  max-width: 400px;
  width: auto;
  height: auto;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}
.attachment-grid {
  display: grid;
  gap: 4px;
}
.attachment-grid.grid-5 {
  grid-template-columns: repeat(5, minmax(140px, 1fr));
}
.attachment-grid.grid-4 {
  grid-template-columns: repeat(4, minmax(140px, 1fr));
}
.attachment-grid.grid-3 {
  grid-template-columns: repeat(3, minmax(140px, 1fr));
}
.attachment-grid.grid-2 {
  grid-template-columns: repeat(2, minmax(140px, 1fr));
}
.attachment-grid-item {
  cursor: pointer;
  background: #ecf1f4;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  width: 100%;
}
</style>
